import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import {
  CreateMicroserviceWarehouseDto,
  WarehouseDetailsType,
  WarehouseListItemType,
  WarehouseType,
} from '../../types/warehouse/warehouse.type';
import { AddressType } from '../../types/address/address.type';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const postMicroserviceWarehouseDetails = createAsyncThunk<
  Pick<WarehouseType, 'id' | 'created_at' | 'modified_at' | 'name'> & {
    address: { id: number };
    organization: { id: number };
  },
  { data: CreateMicroserviceWarehouseDto },
  { state: RootReducerState }
>('warehouses/postMicroserviceWarehouseDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<
      Pick<WarehouseType, 'id' | 'created_at' | 'modified_at' | 'name'> & {
        address: { id: number };
        organization: { id: number };
      }
    >(`${process.env.REACT_APP_API_URL}/inventory/warehouse`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating warehouse!' });
  }
});

export const getMicroserviceWarehousesList = createAsyncThunk<
  PaginationType<WarehouseListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('warehouses/getMicroserviceWarehousesList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      null,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );

    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<WarehouseListItemType>>(
      `${process.env.REACT_APP_API_URL}/inventory/warehouse${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching warehouses!' });
  }
});

export const getMicroserviceWarehouseDetails = createAsyncThunk<
  WarehouseDetailsType,
  { id: number },
  {
    state: RootReducerState;
  }
>('warehouses/getMicroserviceWarehouseDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<WarehouseDetailsType>(
      `${process.env.REACT_APP_API_URL}/inventory/warehouse/${id}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching warehouse!' });
  }
});

export const patchMicroserviceWarehouseDetails = createAsyncThunk<
  Pick<WarehouseType, 'id' | 'created_at' | 'modified_at' | 'name'> & {
    address: AddressType;
  },
  { id: number; data: Partial<CreateMicroserviceWarehouseDto> },
  { state: RootReducerState }
>('warehouses/patchMicroserviceWarehouseDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<
      Pick<WarehouseType, 'id' | 'created_at' | 'modified_at' | 'name'> & {
        address: AddressType;
      }
    >(`${process.env.REACT_APP_API_URL}/inventory/warehouse/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating warehouse!' });
  }
});

export const deleteMicroserviceWarehouseDetails = createAsyncThunk<
  Pick<WarehouseType, 'id' | 'created_at' | 'modified_at'>,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('warehouses/deleteMicroserviceWarehouseDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<Pick<WarehouseType, 'id' | 'created_at' | 'modified_at'>>(
      `${process.env.REACT_APP_API_URL}/inventory/warehouse/${id}`
    );
    if (fetchList)
      thunkAPI.dispatch(
        getMicroserviceWarehousesList(thunkAPI.getState().warehousesReducer.microserviceWarehousesRouteParams)
      );
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting warehouse!' });
  }
});
